/**
 * CAD viewer model.
 *
 * Three concerns are deliberately kept apart:
 *
 *  1. **Geometry** — the GLB file. Produced by `pnpm cad:convert`, it only carries shapes and a
 *     node name per selectable part. It contains no business information.
 *  2. **Business metadata** — the *manifest*. Hand maintained (or, later, served by the API).
 *     It is keyed on GLB node names and is the only place where labels, references and
 *     categories live. Nothing here may be inferred from the geometry.
 *  3. **Real-time state** — connectivity, alerts, maintenance. Not part of this MVP; `dataKey`
 *     exists so that a part can later be joined to such a feed without touching the geometry or
 *     the manifest layout.
 */

/** A 3D vector, in glTF space (metres, Y up, right handed). */
export type Vec3 = readonly [number, number, number];

/** One editable business entry, matched to the GLB through `nodeName`. */
export interface CadPartManifestEntry {
  /** Stable business identifier. Must be unique inside a manifest. */
  id: string;
  /** Name of the GLB node holding this part's geometry. Must be unique inside a manifest. */
  nodeName: string;
  /** Human readable name shown in the part list and the details panel. */
  label: string;
  /** Commercial or ERP reference, when known. */
  reference?: string;
  /** Free-form grouping used by the part list (`Motorisation`, `Sécurité`, ...). */
  category?: string;
  description?: string;
  /** Whether the part can be hovered and selected. Defaults to `true`. */
  selectable?: boolean;
  /** Whether the part stays in place in the exploded view. Defaults to `false`. */
  fixed?: boolean;
  /** Overrides the automatic radial explode direction. Needs not be normalised. */
  explodeVector?: Vec3;
  /** Multiplies the explode distance for this part. Defaults to `1`. */
  explodeDistance?: number;
  /** Join key towards a future real-time state feed. Never used for geometry. */
  dataKey?: string;
}

/** The editable manifest file, one per CAD model. */
export interface CadModelManifest {
  /**
   * Informative only: at runtime the URL always comes from the API, so that authorisation and
   * URL expiry stay under the backend's control.
   */
  modelUrl?: string;
  /** Optional human note, e.g. which values still need to be filled in. */
  comment?: string;
  parts: CadPartManifestEntry[];
}

/** Defaults applied when the manifest leaves a field out. */
export const CAD_PART_DEFAULT_SELECTABLE = true;
export const CAD_PART_DEFAULT_FIXED = false;
export const CAD_PART_DEFAULT_EXPLODE_DISTANCE = 1;

/**
 * A part as consumed by the viewer: a manifest entry with every default resolved, plus the
 * knowledge of whether the manifest actually described it.
 */
export interface CadViewerPart {
  id: string;
  nodeName: string;
  label: string;
  reference?: string;
  category?: string;
  description?: string;
  dataKey?: string;
  selectable: boolean;
  fixed: boolean;
  explodeVector?: Vec3;
  explodeDistance: number;
  /**
   * `false` when the part exists in the GLB but no manifest entry describes it. Such parts stay
   * usable in the viewer, with the raw node name as label, but the details panel makes clear
   * that no business data is available.
   */
  hasManifestEntry: boolean;
}
